/**
 * Enterprise-grade module-based permission system
 * Follows Salesforce/HubSpot/Zoho pattern: Module + Action + Data Scope
 * 
 * Modules: accounts, contacts, deals
 * Actions: view, create, edit, delete, assign, export, close, reopen
 * Scopes: all, own, team, assigned
 */

import { normalizeRole, getDataScope, isAdminRole, isCLevel } from './Roles.js';

// ============================================================================
// PERMISSION DEFINITIONS BY MODULE
// ============================================================================

export const ACCOUNTS_PERMISSIONS = {
    VIEW_ALL: 'accounts:view:all',
    VIEW_OWN: 'accounts:view:own',
    CREATE: 'accounts:create',
    EDIT_OWN: 'accounts:edit:own',
    EDIT_TEAM: 'accounts:edit:team',
    DELETE: 'accounts:delete',
    ASSIGN_OWNER: 'accounts:assign',
    EXPORT: 'accounts:export'
};

export const CONTACTS_PERMISSIONS = {
    VIEW_ALL: 'contacts:view:all',
    VIEW_OWN: 'contacts:view:own',
    CREATE: 'contacts:create',
    EDIT_OWN: 'contacts:edit:own',
    EDIT_TEAM: 'contacts:edit:team',
    DELETE: 'contacts:delete',
    ASSIGN_OWNER: 'contacts:assign',
    EXPORT: 'contacts:export'
};

export const DEALS_PERMISSIONS = {
    VIEW_ALL: 'deals:view:all',
    VIEW_OWN: 'deals:view:own',
    CREATE: 'deals:create',
    EDIT_OWN: 'deals:edit:own',
    EDIT_TEAM: 'deals:edit:team',
    DELETE: 'deals:delete',
    ASSIGN_OWNER: 'deals:assign',
    CLOSE_WON_LOST: 'deals:close',
    REOPEN: 'deals:reopen',
    EXPORT: 'deals:export',
    REVENUE_SETUP: 'deals:revenue',
    COMMISSION: 'deals:commission'
};

export const CALENDAR_PERMISSIONS = {
    VIEW: 'calendar:view',
    CREATE: 'calendar:create',
    EDIT: 'calendar:edit',
};

export const DASHBOARD_PERMISSIONS = {
    VIEW_COMPANY: 'dashboard:company',
    VIEW_TEAM: 'dashboard:team',
    VIEW_OWN: 'dashboard:own',
    VIEW_ASSIGNED: 'dashboard:assigned',
    CUSTOMIZE: 'dashboard:customize'
};

export const REPORTS_PERMISSIONS = {
    VIEW_ALL: 'reports:view:all',
    VIEW_TEAM: 'reports:view:team',
    VIEW_OWN: 'reports:view:own',
    EXPORT: 'reports:export'
};

export const USERS_PERMISSIONS = {
    VIEW: 'users:view',
    CREATE: 'users:create',
    EDIT: 'users:edit',
    DELETE: 'users:delete',
    MANAGE_ROLES: 'users:roles',
    ASSIGN_ADMIN: 'users:assign_admin',
    ASSIGN_SUPER_ADMIN: 'users:assign_super_admin'
};

export const SETTINGS_PERMISSIONS = {
    VIEW: 'settings:view',
    MANAGE: 'settings:manage'
};

export const REVENUE_PERMISSIONS = {
    VIEW: 'revenue:view',
    CREATE: 'revenue:create',
    EDIT: 'revenue:edit',
    DELETE: 'revenue:delete',
    VIEW_AMOUNT: 'revenue:view_amount',
    EXPORT: 'revenue:export'
};

export const GOALS_PERMISSIONS = {
    VIEW: 'goals:view',
    CREATE: 'goals:create',
    EDIT: 'goals:edit',
    DELETE: 'goals:delete',
    VIEW_AMOUNT: 'goals:view_amount',
    EXPORT: 'goals:export'
};

export const COMMISSIONS_PERMISSIONS = {
    VIEW: 'commissions:view',
    CREATE: 'commissions:create',
    EDIT: 'commissions:edit',
    DELETE: 'commissions:delete',
    VIEW_AMOUNT: 'commissions:view_amount',
    VIEW_PROFIT_PCT: 'commissions:view_profit_pct',
    EXPORT: 'commissions:export'
};
export const PERMISSIONS = {
    ...ACCOUNTS_PERMISSIONS,
    ...CONTACTS_PERMISSIONS,
    ...DEALS_PERMISSIONS,
    ...CALENDAR_PERMISSIONS,
    ...REVENUE_PERMISSIONS,
    ...GOALS_PERMISSIONS,
    ...COMMISSIONS_PERMISSIONS,
    ...DASHBOARD_PERMISSIONS,
    ...REPORTS_PERMISSIONS,
    ...USERS_PERMISSIONS,
    ...SETTINGS_PERMISSIONS
};

// ============================================================================
// ROLE-BASED PERMISSION MATRIX
// ============================================================================

export const ROLE_PERMISSIONS = {
    // Super Admin — unrestricted access is a hardcoded bypass in PermissionService.can().
    // Do not seed matrix rows; leave empty so fallback never fabricates a full checkbox grid.
    super_admin: {},

    // Admin - Full CRM access
    admin: {
        accounts: ['view:all', 'create', 'edit:all', 'delete', 'assign', 'export'],
        contacts: ['view:all', 'create', 'edit:all', 'delete', 'assign', 'export'],
        deals: ['view:all', 'create', 'edit:all', 'delete', 'assign', 'close', 'reopen', 'export', 'revenue', 'commission'],
        calendar: ['view', 'create', 'edit'],
        revenue: ['view', 'create', 'edit', 'delete', 'view_amount', 'export'],
        goals: ['view', 'create', 'edit', 'delete', 'view_amount', 'export'],
        commissions: ['view', 'create', 'edit', 'delete', 'view_amount', 'view_profit_pct', 'export'],
        dashboard: ['company', 'team', 'own', 'assigned', 'customize'],
        reports: ['view:all', 'export'],
        users: ['view', 'create', 'edit', 'delete', 'roles', 'assign_admin'],
        settings: ['view', 'manage'],
        permissions: ['view', 'edit'],
        data_management: ['deals', 'accounts', 'contacts', 'users', 'goals', 'commissions', 'activities', 'import_deals', 'import_accounts', 'import_contacts']
    },

    // CEO - Operational executive with full CRUD on core modules
    ceo: {
        accounts: ['view:all', 'create', 'edit:all', 'delete', 'view_amount', 'export', 'assign'],
        contacts: ['view:all', 'create', 'edit:all', 'delete', 'export'],
        deals: ['view:all', 'create', 'edit:all', 'delete', 'view_amount', 'export', 'assign'],
        calendar: ['view', 'create', 'edit'],
        revenue: ['view', 'view_amount'],
        goals: ['view:all', 'create', 'edit:all', 'delete', 'view_amount', 'export', 'assign'],
        commissions: ['view', 'create', 'edit', 'delete', 'view_amount', 'export'],
        dashboard: ['company', 'view_amount'],
        reports: ['view:all', 'view_amount', 'export'],
        users: ['view', 'create', 'edit', 'delete', 'export'],
        settings: ['view']
    },

    // Sales Executive - Individual contributor
    sales_executive: {
        accounts: ['view:own', 'create', 'edit:own', 'view_amount'],
        contacts: ['view:own', 'create', 'edit:own'],
        deals: ['view:own', 'create', 'edit:own', 'close', 'view_amount'],
        calendar: ['view', 'create', 'edit'],
        revenue: ['view', 'view_amount'],
        goals: ['view'],
        commissions: [],
        dashboard: ['own', 'view_amount'],
        reports: ['view:own'],
        users: [],
        settings: []
    },

    // Account Manager — placeholder, configure via role permissions UI
    account_manager: {
        accounts: [],
        contacts: [],
        deals: [],
        revenue: [],
        goals: [],
        commissions: [],
        dashboard: [],
        reports: [],
        users: [],
        settings: []
    },

    // Delivery Manager — placeholder, configure via role permissions UI
    delivery_manager: {
        accounts: [],
        contacts: [],
        deals: [],
        revenue: [],
        goals: [],
        commissions: [],
        dashboard: [],
        reports: [],
        users: [],
        settings: []
    },

    // Finance — placeholder, configure via role permissions UI
    finance: {
        accounts: [],
        contacts: [],
        deals: [],
        revenue: [],
        goals: [],
        commissions: [],
        dashboard: [],
        reports: [],
        users: [],
        settings: []
    },

    // CRM Administrator — placeholder, configure via role permissions UI
    crm_administrator: {
        accounts: [],
        contacts: [],
        deals: [],
        revenue: [],
        goals: [],
        commissions: [],
        dashboard: [],
        reports: [],
        users: [],
        settings: []
    },

    // Viewer - Read-only
    viewer: {
        accounts: ['view:all'],
        contacts: ['view:all'],
        deals: ['view:all'],
        calendar: ['view'],
        revenue: ['view'],
        goals: [],
        commissions: [],
        dashboard: ['company'],
        reports: ['view:all'],
        users: [],
        settings: ['view']
    }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get permissions for a role in a specific module
 */
export function getModulePermissions(role, module) {
    const normalizedRole = normalizeRole(role);
    const rolePerms = ROLE_PERMISSIONS[normalizedRole] || ROLE_PERMISSIONS[role] || {};
    return rolePerms[module] || [];
}

/**
 * Check if role has specific permission
 */
export function hasPermission(role, permission) {
    // Defensive: some callers pass undefined when permission constants are missing/mismatched.
    if (!permission || typeof permission !== 'string') return false;

    const parts = permission.split(':');
    const module = parts[0];
    const action = parts[1];
    const scope = parts[2];

    if (!module || !action) return false;

    const modulePerms = getModulePermissions(role, module);

    // Check for exact match
    if (modulePerms.includes(action) || modulePerms.includes(`${action}:${scope}`)) {
        return true;
    }

    // Check for wildcard permissions (e.g., 'edit:all' grants 'edit:team' and 'edit:own')
    if (scope) {
        const hasWildcard = modulePerms.some(p => p === action || p.startsWith(`${action}:`));
        if (hasWildcard) return true;

        // Scope hierarchy: all > team > own
        if (scope === 'team' && modulePerms.includes(`${action}:all`)) return true;
        if (scope === 'own' && (modulePerms.includes(`${action}:all`) || modulePerms.includes(`${action}:team`))) return true;
    }

    return false;
}

/**
 * Check if user can access data at required scope
 */
export function canAccessScope(userRole, requiredScope) {
    const userScope = getDataScope(userRole);
    
    const scopeHierarchy = {
        'own': 1,
        'assigned': 2,
        'team': 3,
        'department': 4,
        'company': 5
    };
    
    const userLevel = scopeHierarchy[userScope] || 0;
    const requiredLevel = scopeHierarchy[requiredScope] || 0;
    
    return userLevel >= requiredLevel;
}

/**
 * Get data scope for filtering records
 */
export function getDataScopeForRole(role) {
    return getDataScope(role);
}

/**
 * Check if role can perform action on module
 */
export function canPerformAction(role, module, action, requiredScope = null) {
    const modulePerms = getModulePermissions(role, module);
    
    // Check if action is granted
    const hasAction = modulePerms.includes(action) || 
                      modulePerms.includes(`${action}:all`) ||
                      modulePerms.includes(`${action}:team`) ||
                      modulePerms.includes(`${action}:own`);
    
    if (!hasAction) return false;
    
    // If scope is required, check scope access
    if (requiredScope) {
        return canAccessScope(role, requiredScope);
    }
    
    return true;
}

/**
 * Get all permissions for a role (flattened)
 */
export function permissionsForRole(role) {
    const normalizedRole = normalizeRole(role);
    const rolePerms = ROLE_PERMISSIONS[normalizedRole] || ROLE_PERMISSIONS[role] || {};
    
    const allPerms = [];
    Object.entries(rolePerms).forEach(([module, actions]) => {
        actions.forEach(action => {
            allPerms.push(`${module}:${action}`);
        });
    });
    
    return allPerms;
}
